import { useI18n } from 'vue-i18n';
import { useTokenStore, useUserStore } from '@/plugins/stores';
import NavigationHelper from '@/util/NavigationHelper';
import { useRouter } from 'vue-router';
import { Forms } from '@/enums/Forms';
import { onBeforeMount, Ref, ref, watch } from 'vue';
import UserInfo from '@/models/UserInfo';
import countries from 'i18n-iso-countries';
import i18n_iso_countries_en from 'i18n-iso-countries/langs/en.json';
import i18n_iso_countries_nl from 'i18n-iso-countries/langs/nl.json';
import UserInfoController from '@/controllers/UserInfoController';
import AuthenticationHelper from '@/util/AuthenticationHelper';
import PageHelper from '@/util/PageHelper';
import ValidationRules from '@/util/ValidationRules';
import PolteqButton from '@/components/PolteqButton/PolteqButton.vue';
import PolteqTextField from '@/components/PolteqTextField/PolteqTextField.vue';
import { VForm } from 'vuetify/components';
import Response from '@/models/Response';

export default {
  name: 'PersonalProfile',
  components: {
    PolteqButton,
    PolteqTextField
  },
  computed: {
    Forms() {
      return Forms;
    },
    NavigationHelper() {
      return NavigationHelper;
    }
  },
  setup: function () {
    const i18n = useI18n();
    if (i18n.locale.value === 'nl') {
      countries.registerLocale(i18n_iso_countries_nl);
    } else {
      countries.registerLocale(i18n_iso_countries_en);
    }
    const officialCountryNames = countries.getNames(i18n.locale.value, { select: 'official' });
    const countryNames = ref(Object.keys(officialCountryNames).map((key) => officialCountryNames[key]));
    watch(i18n.locale, () => {
      if (i18n.locale.value === 'nl') {
        countries.registerLocale(i18n_iso_countries_nl);
      } else {
        countries.registerLocale(i18n_iso_countries_en);
      }
      const newOfficialCountryNames = countries.getNames(i18n.locale.value, { select: 'official' });
      countryNames.value = Object.keys(newOfficialCountryNames).map((key: string) => newOfficialCountryNames[key]);
    });
    const userStore = useUserStore();
    const router = useRouter();
    const edit = ref(false);
    const userInfo = ref(setProfile());
    const cancelEdit = () => {
      edit.value = false;
      userInfo.value = setProfile();
    };
    const rules = ValidationRules.getRules();
    const updateProfileForm = ref() as Ref<VForm>;
    const saveProfile = async (updatedUserInfo: UserInfo) => {
      await updateProfileForm.value.validate();
      if (updateProfileForm.value.isValid) {
        edit.value = false;
        await persistProfile(updatedUserInfo);
        userInfo.value = setProfile();
      }
    };
    onBeforeMount(() => {
      watch(
        () => userStore.userInfo,
        () => {
          userInfo.value = setProfile();
        }
      );
    });
    return {
      countryNames,
      userInfo,
      cancelEdit,
      rules,
      updateProfileForm,
      saveProfile,
      edit,
      i18n,
      userStore,
      router
    };
  }
};

function setProfile(): UserInfo {
  const userStore = useUserStore();
  return { ...userStore.userInfo };
}

async function persistProfile(userInfo: UserInfo) {
  const tokenStore = useTokenStore();
  const response: Response = await UserInfoController.updateProfile(
    tokenStore.accessToken,
    userInfo.givenName,
    userInfo.familyName,
    userInfo.phoneNumber,
    userInfo.address
  );
  PageHelper.setNotificationMessage(response);
  AuthenticationHelper.setTokens(response);
  await AuthenticationHelper.getUserInfo();
}
